const lower = (value) => String(value ?? '').trim().toLowerCase();

const withoutSurface = (value) => {
  const normalized = lower(value);
  return normalized.startsWith('custom:') ? normalized.slice('custom:'.length) : normalized;
};

const originsOf = (node) => (
  Array.isArray(node?.surfaces) && node.surfaces.length
    ? node.surfaces
    : (node?.platform ? [node.platform] : [])
);

const onCustomSurface = (node) => originsOf(node).includes('custom');

const endpointId = (endpoint) => (
  typeof endpoint === 'object' && endpoint !== null ? endpoint.id : endpoint
);

const responseRows = (response, key) => {
  const rows = Array.isArray(response) ? response : response?.[key];
  return Array.isArray(rows) ? rows : [];
};

const agentKeys = (node) => [
  node?.agent_identifier,
  node?.agent_id,
  node?.numeric_id,
  node?.label,
  node?.name,
  node?.id,
].map(withoutSurface).filter(Boolean);

const inventoryKey = (row) => (
  withoutSurface(row?.agent_identifier || row?.id || row?.name)
);

const numericAgentKey = (row) => [
  row?.numeric_id,
  row?.agent_id,
  withoutSurface(row?.id),
  row?.name,
].map((value) => String(value ?? '').trim()).find((value) => /^\d+$/.test(value));

const modelKey = (value) => lower(value);

const modelRows = (metrics) => {
  if (Array.isArray(metrics?.model_performance) && metrics.model_performance.length) {
    return metrics.model_performance
      .filter((row) => row?.model)
      .map((row) => ({ name: row.model, requests: Number(row.requests) || 0 }));
  }
  const labels = metrics?.model_distribution?.labels || [];
  const values = metrics?.model_distribution?.values || [];
  return labels.map((name, index) => ({ name, requests: Number(values[index]) || 0 }));
};

const syntheticId = (type, value) => (
  `telemetry:custom:${type}:${encodeURIComponent(String(value))}`
);

const TRACE_NODE_TYPES = new Set([
  'agent', 'subagent', 'tool', 'model', 'user', 'app', 'framework', 'mcp', 'datasource',
]);

const traceNodeType = (value) => {
  const type = lower(value);
  if (type === 'application') return 'app';
  if (type === 'mcpserver' || type === 'mcp_server') return 'mcp';
  return TRACE_NODE_TYPES.has(type) ? type : null;
};

const newestTimestamp = (...values) => {
  const candidates = values.filter(Boolean).map(String).sort();
  return candidates.length ? candidates[candidates.length - 1] : null;
};

/** Collapse legacy numeric-keyed graph agents into their canonical registry identity. */
function normalizeRegisteredAgents(nodes, links, registryByIdentifier, registryByNumericId) {
  const customAgents = nodes.filter((node) => node.type === 'agent' && onCustomSurface(node));
  const canonicalByIdentifier = new Map();
  customAgents.forEach((node) => {
    agentKeys(node).filter((key) => !/^\d+$/.test(key)).forEach((key) => {
      if (registryByIdentifier.has(key) && !canonicalByIdentifier.has(key)) {
        canonicalByIdentifier.set(key, node);
      }
    });
  });

  const redirects = new Map();
  const replacements = new Map();
  customAgents.forEach((node) => {
    const numericId = [node.numeric_id, node.agent_id, node.label, node.name]
      .map((value) => String(value ?? '').trim())
      .find((value) => /^\d+$/.test(value));
    if (!numericId) return;
    const reg = registryByNumericId.get(numericId);
    if (!reg) return;
    const identifier = lower(reg.agent_identifier || reg.service_name);
    if (!identifier) return;
    const canonical = canonicalByIdentifier.get(identifier);
    if (canonical && canonical.id !== node.id) {
      redirects.set(node.id, canonical.id);
      const current = replacements.get(canonical.id) || canonical;
      replacements.set(canonical.id, {
        ...current,
        label: reg.agent_name || current.label || identifier,
        agent_id: identifier,
        agent_identifier: identifier,
        numeric_id: reg.agent_id ?? reg.id ?? numericId,
        call_count: (Number(current.call_count) || 0) + (Number(node.call_count) || 0),
        degree: (Number(current.degree) || 0) + (Number(node.degree) || 0),
        last_seen: newestTimestamp(current.last_seen, node.last_seen),
      });
      return;
    }
    const replacement = {
      ...node,
      label: reg.agent_name || identifier,
      agent_id: identifier,
      agent_identifier: identifier,
      numeric_id: reg.agent_id ?? reg.id ?? numericId,
    };
    replacements.set(node.id, replacement);
    canonicalByIdentifier.set(identifier, replacement);
  });

  if (!redirects.size && !replacements.size) return { nodes, links };
  const outputNodes = nodes
    .filter((node) => !redirects.has(node.id))
    .map((node) => replacements.get(node.id) || node);
  const linkKeys = new Set();
  const outputLinks = [];
  links.forEach((link) => {
    const source = redirects.get(endpointId(link.source)) || endpointId(link.source);
    const target = redirects.get(endpointId(link.target)) || endpointId(link.target);
    if (!source || !target || source === target) return;
    const key = [source, target, link.rel_type || link.type].join('|');
    if (linkKeys.has(key)) return;
    linkKeys.add(key);
    outputLinks.push({ ...link, source, target });
  });
  return { nodes: outputNodes, links: outputLinks };
}

/**
 * Merge the trace-derived Custom topology without letting the registry's inactive agents
 * leak into a dated view. Only relationships whose Agent endpoint resolves to one of the
 * selected-window active agents are admitted. Models already reconciled from LLM Metrics
 * are reused by name, so a trace relationship cannot create a second model lane entry.
 */
function mergeTraceTopology(nodes, links, traceTopology, metricsKnown) {
  const traceNodes = responseRows(traceTopology, 'nodes');
  const traceLinks = responseRows(traceTopology, 'links');
  if (!traceNodes.length || !traceLinks.length) return { nodes, links };

  const activeAgents = nodes.filter((node) => node.type === 'agent' && onCustomSurface(node));
  const agentsByNumericId = new Map();
  const agentsByKey = new Map();
  activeAgents.forEach((agent) => {
    if (agent.numeric_id !== undefined && agent.numeric_id !== null) {
      agentsByNumericId.set(String(agent.numeric_id), agent);
    }
    agentKeys(agent).forEach((key) => agentsByKey.set(key, agent));
  });

  const traceById = new Map(traceNodes.map((node) => [String(node.id), node]));
  const mappedAgents = new Map();
  traceNodes.filter((node) => traceNodeType(node.type) === 'agent').forEach((node) => {
    const idMatch = String(node.id || '').match(/^agent-(.+)$/);
    const numericCandidates = [node.numeric_id, node.agent_id, idMatch?.[1]]
      .filter((value) => value !== undefined && value !== null)
      .map(String);
    const keyCandidates = agentKeys(node);
    const matched = numericCandidates.map((key) => agentsByNumericId.get(key)).find(Boolean)
      || keyCandidates.map((key) => agentsByKey.get(key)).find(Boolean);
    if (matched) mappedAgents.set(String(node.id), matched);
  });

  const existingBySignature = new Map();
  nodes.forEach((node) => {
    const type = traceNodeType(node.type);
    const key = lower(node.label || node.name);
    if (type && key) existingBySignature.set(`${type}|${key}`, node);
  });

  const outputNodes = [...nodes];
  const outputLinks = [...links];
  const materialized = new Map();
  const resolveAsset = (traceNode) => {
    const type = traceNodeType(traceNode?.type);
    const label = traceNode?.label || traceNode?.display_name || traceNode?.name;
    if (!type || type === 'agent' || !label) return null;
    const signature = `${type}|${lower(label)}`;
    const existing = existingBySignature.get(signature);
    if (existing) return existing;
    // LLM Metrics is authoritative for the selected window. If a model is absent
    // there, do not resurrect it from a stale or capped trace-topology response.
    if (type === 'model' && metricsKnown) return null;
    if (materialized.has(signature)) return materialized.get(signature);
    const node = {
      ...traceNode,
      id: syntheticId(type, traceNode.name || traceNode.id || label),
      label,
      type,
      // Ops datasource `source_type` means file/database/etc.; on graph nodes that
      // property means the Neo4j entity label. Keep the datasource subtype without
      // making the detail panel mistake this synthetic id for a graph id.
      datasource_source_type: type === 'datasource' ? traceNode.source_type : undefined,
      source_type: undefined,
      entity_type: undefined,
      platform: 'custom',
      surfaces: ['custom'],
      telemetry_sourced: true,
    };
    materialized.set(signature, node);
    existingBySignature.set(signature, node);
    outputNodes.push(node);
    return node;
  };

  const linkKeys = new Set(outputLinks.map((link) => [
    endpointId(link.source), endpointId(link.target), link.rel_type || link.type,
  ].join('|')));

  traceLinks.forEach((link) => {
    const sourceId = String(endpointId(link.source));
    const targetId = String(endpointId(link.target));
    const sourceTrace = traceById.get(sourceId);
    const targetTrace = traceById.get(targetId);
    const sourceAgent = mappedAgents.get(sourceId);
    const targetAgent = mappedAgents.get(targetId);

    // The ops topology currently emits direct Agent<->asset relationships. Requiring
    // one mapped endpoint prevents its 33 registered agents (including idle ones) from
    // changing the active count supplied by Agent Atlas.
    if (!sourceAgent && !targetAgent) return;
    const source = sourceAgent || resolveAsset(sourceTrace);
    const target = targetAgent || resolveAsset(targetTrace);
    if (!source || !target || source.id === target.id) return;
    const type = link.type || link.rel_type || 'related_to';
    const relType = link.rel_type || String(type).toUpperCase();
    const key = [source.id, target.id, relType].join('|');
    if (linkKeys.has(key)) return;
    linkKeys.add(key);
    outputLinks.push({
      ...link,
      source: source.id,
      target: target.id,
      type,
      rel_type: relType,
      telemetry_sourced: true,
    });
  });

  return { nodes: outputNodes, links: outputLinks };
}

/**
 * Reconcile the selected-window custom topology with the sources used by Dashboard.
 * Neo4j remains authoritative for graph-only entities. Agent Atlas supplies the active
 * agents, management their display/numeric identity, and LLM Metrics the models and
 * per-agent USES_MODEL relationships.
 */
export function reconcileCustomTopology(topology, {
  inventory,
  registry,
  llmMetrics,
  traceTopology,
  inventoryKnown = false,
  metricsKnown = false,
} = {}) {
  let nodes = [...(topology?.nodes || [])];
  let links = [...(topology?.links || [])];

  const registered = responseRows(registry, 'agents');
  const registryByIdentifier = new Map();
  const registryByNumericId = new Map();
  registered.forEach((row) => {
    [row?.agent_identifier, row?.service_name].forEach((value) => {
      const identifier = lower(value);
      if (identifier) registryByIdentifier.set(identifier, row);
    });
    const numericId = row?.agent_id ?? row?.id;
    if (numericId !== undefined && numericId !== null) {
      registryByNumericId.set(String(numericId), row);
    }
  });
  ({ nodes, links } = normalizeRegisteredAgents(
    nodes, links, registryByIdentifier, registryByNumericId,
  ));

  const usageRows = Array.isArray(llmMetrics?.agent_model_usage)
    ? llmMetrics.agent_model_usage : [];
  const usageByIdentifier = new Map();
  const usageByNumericId = new Map();
  usageRows.forEach((row) => {
    if (row?.agent_identifier) usageByIdentifier.set(lower(row.agent_identifier), row);
    if (row?.agent_id !== undefined && row?.agent_id !== null) {
      usageByNumericId.set(String(row.agent_id), row);
    }
  });

  if (inventoryKnown) {
    const existingAgents = nodes.filter(
      (node) => node.type === 'agent' && onCustomSurface(node),
    );
    const existingByKey = new Map();
    existingAgents.forEach((node) => {
      agentKeys(node).forEach((key) => {
        if (!existingByKey.has(key)) existingByKey.set(key, node);
      });
    });

    const activeAgents = [];
    const activeIndexById = new Map();
    responseRows(inventory, 'items').forEach((row) => {
      const key = inventoryKey(row);
      if (!key) return;
      const numericKey = numericAgentKey(row);
      const reg = registryByIdentifier.get(key)
        || (numericKey ? registryByNumericId.get(numericKey) : null);
      // Historical telemetry sometimes used only the numeric database id as the
      // agent identity. Without the registry bridge it is not a usable agent name and
      // rendering it creates the exact duplicate rows ("34", "49", ...) we avoid.
      if (/^\d+$/.test(key) && !reg) return;
      const numericId = reg?.agent_id ?? reg?.id ?? row?.numeric_id;
      const usage = usageByIdentifier.get(key)
        || (numericId !== undefined && numericId !== null
          ? usageByNumericId.get(String(numericId)) : null);
      const canonicalKey = withoutSurface(reg?.agent_identifier || reg?.service_name || key);
      const existing = existingByKey.get(canonicalKey) || existingByKey.get(key);
      const stableIdentifier = reg?.agent_identifier || row.agent_identifier
        || withoutSurface(row.id) || row.name;
      const displayName = reg?.agent_name || row.display_name || row.agent_name
        || usage?.agent_name || row.name || stableIdentifier;
      const activity = Number(row.size ?? row.span_count ?? usage?.span_count);
      const node = existing ? {
        ...existing,
        label: displayName,
        agent_identifier: stableIdentifier,
        numeric_id: numericId ?? existing.numeric_id,
        call_count: Number.isFinite(activity) ? activity : existing.call_count,
        observed: true,
      } : {
        id: syntheticId('Agent', stableIdentifier),
        label: displayName,
        type: 'agent',
        platform: 'custom',
        surfaces: ['custom'],
        agent_id: stableIdentifier,
        agent_identifier: stableIdentifier,
        numeric_id: numericId,
        call_count: Number.isFinite(activity) ? activity : 0,
        degree: Number.isFinite(activity) ? activity : 0,
        last_seen: row.last_seen || null,
        observed: true,
        telemetry_sourced: true,
      };
      const duplicateIndex = activeIndexById.get(node.id);
      if (duplicateIndex !== undefined) {
        const previous = activeAgents[duplicateIndex];
        const mergedActivity = (Number(previous.call_count) || 0)
          + (Number.isFinite(activity) ? activity : 0);
        activeAgents[duplicateIndex] = {
          ...previous,
          call_count: mergedActivity,
          degree: mergedActivity,
          last_seen: newestTimestamp(previous.last_seen, row.last_seen),
        };
        return;
      }
      activeIndexById.set(node.id, activeAgents.length);
      activeAgents.push(node);
    });

    const replacedIds = new Set(existingAgents.map((node) => node.id));
    nodes = [
      ...nodes.filter((node) => !replacedIds.has(node.id)),
      ...activeAgents,
    ];
    const kept = new Set(nodes.map((node) => node.id));
    links = links.filter(
      (link) => kept.has(endpointId(link.source)) && kept.has(endpointId(link.target)),
    );
  }

  if (metricsKnown) {
    const existingModels = nodes.filter((node) => node.type === 'model');
    const existingByName = new Map();
    existingModels.forEach((node) => {
      const key = modelKey(node.label || node.name);
      if (key && !existingByName.has(key)) existingByName.set(key, node);
    });

    const activeModels = [];
    const usedNodeIds = new Set();
    modelRows(llmMetrics).forEach(({ name, requests }) => {
      const key = modelKey(name);
      if (!key) return;
      const existing = existingByName.get(key);
      const node = existing ? {
        ...existing,
        label: name,
        platform: existing.platform || 'custom',
        surfaces: [...new Set([...(existing.surfaces || []), 'custom'])],
        call_count: requests,
      } : {
        id: syntheticId('Model', name),
        label: name,
        type: 'model',
        platform: 'custom',
        surfaces: ['custom'],
        call_count: requests,
        degree: requests,
        telemetry_sourced: true,
      };
      if (usedNodeIds.has(node.id)) return;
      usedNodeIds.add(node.id);
      activeModels.push(node);
    });

    // LLM Metrics is the selected-window source of truth for Custom model nodes.
    const replacedIds = new Set([
      ...existingModels.filter(onCustomSurface).map((node) => node.id),
      ...usedNodeIds,
    ]);
    nodes = [
      ...nodes.filter((node) => !replacedIds.has(node.id)),
      ...activeModels,
    ];
    const kept = new Set(nodes.map((node) => node.id));
    links = links.filter(
      (link) => kept.has(endpointId(link.source)) && kept.has(endpointId(link.target)),
    );

    const agentsByIdentifier = new Map();
    const agentsByNumericId = new Map();
    nodes.filter((node) => node.type === 'agent' && onCustomSurface(node)).forEach((node) => {
      agentKeys(node).forEach((key) => agentsByIdentifier.set(key, node));
      if (node.numeric_id !== undefined && node.numeric_id !== null) {
        agentsByNumericId.set(String(node.numeric_id), node);
      }
    });
    const modelsByName = new Map(
      nodes.filter((node) => node.type === 'model' && onCustomSurface(node))
        .map((node) => [modelKey(node.label), node]),
    );

    usageRows.forEach((usage) => {
      const agent = agentsByNumericId.get(String(usage.agent_id))
        || agentsByIdentifier.get(lower(usage.agent_identifier));
      if (!agent) return;
      (usage.models || []).forEach((modelUsage) => {
        const model = modelsByName.get(modelKey(modelUsage.model));
        if (!model) return;
        const existing = links.find((link) => (
          endpointId(link.source) === agent.id
          && endpointId(link.target) === model.id
          && (link.rel_type === 'USES_MODEL' || link.type === 'uses_model')
        ));
        if (existing) {
          existing.count = Number(modelUsage.requests) || existing.count || 1;
          return;
        }
        links.push({
          source: agent.id,
          target: model.id,
          type: 'uses_model',
          rel_type: 'USES_MODEL',
          count: Number(modelUsage.requests) || 1,
          telemetry_sourced: true,
        });
      });
    });
  }

  ({ nodes, links } = mergeTraceTopology(nodes, links, traceTopology, metricsKnown));

  return {
    ...topology,
    has_data: nodes.length > 0,
    nodes,
    links,
  };
}

export default reconcileCustomTopology;
